import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import MarkDown from 'brastrap/editorial/markdown/Markdown';
import Icon from 'brastrap/common/icon/Icon';
import { hexToRgb } from '../../utils';

const DEFAULT_BACKGROUND_COLOUR = '#FFFFFF';
const messages = defineMessages({
  expand: { id: 'read-more.expand', defaultMessage: 'Read more' },
  collapse: { id: 'read-more.collapse', defaultMessage: 'Show less' },
});
const getRgbaString = ({ r, g, b, a }) => `rgba(${r}, ${g}, ${b}, ${a})`;

const ReadMore = ({
  backgroundColour = DEFAULT_BACKGROUND_COLOUR,
  collapseButtonText,
  collapsedHeight,
  collapseTextColour,
  content,
  expandButtonText,
  expandTextColour,
  intl: { formatMessage },
  parentBackgroundColour,
  hideIcon,
  centerText,
  faqsHeader,
  faqsIntroduction,
  faqs,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [fullHeight, setFullHeight] = useState(2000);
  const contentRef = useRef(null);

  const updateHeight = useCallback(() => {
    if (contentRef.current) {
      setFullHeight(contentRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [updateHeight]);

  const toggleExpand = () => {
    setExpanded(prev => !prev);
  };
  const rgbBackgroundColour = hexToRgb(
    parentBackgroundColour || backgroundColour
  );

  const gradient = `linear-gradient(to bottom,
${getRgbaString({ ...rgbBackgroundColour, a: 0.25 })},
${getRgbaString({ ...rgbBackgroundColour, a: 0.9 })})`;

  const containerStyle = {};
  if (
    !parentBackgroundColour &&
    backgroundColour !== DEFAULT_BACKGROUND_COLOUR
  ) {
    containerStyle.backgroundColor = backgroundColour;
  }

  return (
    <div style={containerStyle} className="c-read-more__container">
      <div
        ref={contentRef}
        className="c-read-more__content"
        style={{ maxHeight: expanded ? fullHeight : collapsedHeight }}
      >
        <MarkDown content={content} centerText={centerText} />
        {!expanded && (
          <div
            className="c-read-more__fade-overlay"
            style={{ backgroundImage: gradient }}
          />
        )}

        {faqs.length > 0 && (
          <div className="c-read-more__faqs">
            {faqsHeader?.trim() && (
              <h3 className="c-read-more__faq-header">{faqsHeader}</h3>
            )}

            {faqsIntroduction?.trim() && (
              <p className="c-read-more__faq-introduction">
                <MarkDown content={faqsIntroduction} />
              </p>
            )}

            <ul className="c-read-more__faq-list">
              {faqs.map((faq, index) => (
                <li key={index} className="c-read-more__faq">
                  <p className="c-read-more__faq-question">{faq.question}</p>

                  <div className="c-read-more__faq-answer">
                    <MarkDown content={faq.answer} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <button
        className="c-read-more__button-text"
        onClick={toggleExpand}
        style={{
          color: expanded ? collapseTextColour : expandTextColour,
        }}
      >
        {expanded
          ? collapseButtonText || formatMessage(messages.collapse)
          : expandButtonText || formatMessage(messages.expand)}
        <br />

        {!hideIcon && (
          <Icon
            icon="ornament-underline"
            className={
              expanded ? 'c-read-more__less-icon' : 'c-read-more__more-icon'
            }
            fillColour={expanded ? collapseTextColour : expandTextColour}
            size={{ width: 35, height: 29 }}
          />
        )}
      </button>
    </div>
  );
};

ReadMore.defaultProps = {
  backgroundColour: DEFAULT_BACKGROUND_COLOUR,
  faqs: [],
  faqsHeader: '',
  faqsIntroduction: '',
};

ReadMore.propTypes = {
  backgroundColour: PropTypes.string,
  collapseButtonText: PropTypes.string,
  collapsedHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collapseTextColour: PropTypes.string,
  content: PropTypes.string.isRequired,
  expandButtonText: PropTypes.string,
  expandTextColour: PropTypes.string,
  faqsHeader: PropTypes.string,
  faqsIntroduction: PropTypes.string,
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
    })
  ),
  intl: PropTypes.object.isRequired,
  parentBackgroundColour: PropTypes.string,
  hideIcon: PropTypes.bool,
  centerText: PropTypes.bool,
};

export default injectIntl(ReadMore);
